import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AccountSummary, AdminUser, PasswordReset, PlatformAuditEntry, SentInvite, StaffNote, SupportVisit,
  addAccountUserApi, addStaffNoteApi, adminMe, deleteAccountApi, getAccountApi, isInvitePending,
  listAccountUsersApi, listPlatformAudit, listStaffNotesApi, masqueradeApi, platformResendInviteApi,
  platformResetPasswordApi, purgeAccountApi, restoreAccountApi, updateAccountApi,
} from '../../api/admin';
import { AccountBillingView, getAccountBilling } from '../../api/platform';
import { ConsoleBillingFields, atLeastPlatform } from '../../shared/types';
import { FigureTile, RowMenu, SubTabs, TableCard } from '../../components/fs';
import { InviteLinkModal, ResetPasswordModal, invitedMessage } from '../../components/PasswordDialogs';
import { useToast } from '../../components/Toast';
import { PHONE, useMediaQuery } from '../../shared/media';
import { usePlatform } from './PlatformLayout';
import { AccountBillingTab } from './AccountBilling';
import { ChangePlanDialog, changeToast, pendingPriceOf } from './ChangePlanDialog';
import { Card, PlanPricingRows } from './accountPlanRows';
import { ActionPill, AuditTable, actorName, describeForAccount } from './platformAudit';
import {
  AddUserModal, DeleteAccountModal, DeleteNowModal, STATUS_CONSEQUENCE, STATUS_WORD, StatusModal, SupportModeModal,
} from './accountDialogs';
import { TrialRow, convertPrice, monD, monDY, percentRunning, trialPlanOf } from './accountBillingModals';
import { daysUntilInstant, isoPlusDays, localDay, longDate, money, moneyExact, todayIso } from './planDisplay';

/**
 * One customer account, whole — the platform console's account page
 * (docs/PLAYBOOK-account-overview.md, PR 1; design_handoff_account_overview/,
 * screens 02–15).
 *
 * A row of Accounts opens it. The head carries every account-level action —
 * Enter in support mode, Change plan and a ⋯ menu (Logins and passwords ·
 * Change status… · Delete account… or Restore account, and a Super Admin's
 * Delete now) — over four tabs, kept in `?tab=`: Overview (the plan and what
 * they pay, recent activity, staff notes, status, delete), Users (the logins,
 * with reset and resend), Billing (the old billing page's body, which the old
 * URL now redirects to) and Activity (every staff action on the account).
 *
 * A padded page in the console's own frame (CLAUDE.md "The frame"), never a
 * centred column. On a phone the head is the design's back bar, the tabs a
 * segmented control, and every dialog a bottom sheet.
 */

type ConsoleAccount = AccountSummary & Partial<ConsoleBillingFields>;
type Tab = 'overview' | 'users' | 'billing' | 'activity';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'billing', label: 'Billing' },
  { key: 'activity', label: 'Activity' },
];
const tabOf = (raw: string | null): Tab => TABS.find((t) => t.key === raw)?.key ?? 'overview';

/** One dialog at a time (rule 6). */
type Editor = { kind: 'masq' | 'status' | 'delete' | 'purge' } | null;

/** The title a disabled control carries for Staff. */
const ADMIN_ONLY = 'Needs a platform Admin';

const STATUS_INTENT: Record<string, string> = {
  trial: 'warn', active: 'ok', past_due: 'danger', suspended: 'danger', cancelled: 'neutral',
};

/** "May 2026" — the month the account was made, read as that day wherever the viewer is. */
const sinceOf = (created: string | null | undefined) => (created
  ? new Date(`${created.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  : '—');

/** "Just now", "3h ago", "Sep 2" — how long ago, the way a trail is skimmed. */
export function ago(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const mins = Math.floor((now.getTime() - then.getTime()) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
  return then.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(then.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}


/** "30 days left to restore it" — and once the instant has passed, the hourly sweep is what is left. */
export function restoreWindow(deleteAfter: string, now: number = Date.now()): string {
  const n = daysUntilInstant(deleteAfter, now);
  return n === 0 ? 'It is due to be deleted now.' : `${n} day${n === 1 ? '' : 's'} left to restore it.`;
}

/** The account's state, as its head and its list row say it. */
function StatePill({ account, trialEnds }: { account: ConsoleAccount; trialEnds: string | null }) {
  if (account.delete_after) return <span className="fs-pill danger">Deleting · {monD(localDay(account.delete_after))}</span>;
  if (account.trial) {
    const end = trialEnds ?? isoPlusDays(todayIso(), account.trial.days_left);
    return <span className="fs-pill info">Free period · to {monD(end)}</span>;
  }
  if (account.subscription_status === 'trialing') return <span className="fs-pill warn">Trial</span>;
  if (account.subscription_status === 'past_due' || account.status === 'past_due') {
    // The declined card's grace (Ben, 2026-09-27): the day it moves to Free.
    return (
      <span className="fs-pill danger">
        {account.grace_until ? `Past due · Free on ${monD(localDay(account.grace_until))}` : 'Past due'}
      </span>
    );
  }
  return <span className={`fs-pill ${STATUS_INTENT[account.status] || 'neutral'}`}>{STATUS_WORD[account.status] ?? account.status}</span>;
}

export default function AccountOverview() {
  const { id } = useParams();
  const accountId = Number(id);
  const { role, rules } = usePlatform();
  const canManage = atLeastPlatform(role, 'admin');
  const isSuper = atLeastPlatform(role, 'superadmin');
  const phone = useMediaQuery(PHONE);
  const [params, setParams] = useSearchParams();
  const tab = tabOf(params.get('tab'));
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  // Back to the list as it was left: its search, sort and plan filter ride in
  // the URL the row was opened from (PlatformConsole puts it in state).
  const cameFrom = (location.state as { from?: string } | null)?.from;
  const backTo = `/platform${cameFrom && cameFrom.startsWith('?') ? cameFrom : ''}`;

  const [account, setAccount] = useState<ConsoleAccount | null>(null);
  const [billing, setBilling] = useState<AccountBillingView | null>(null);
  const [notes, setNotes] = useState<StaffNote[] | null>(null);
  const [recent, setRecent] = useState<PlatformAuditEntry[] | null>(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [editor, setEditor] = useState<Editor>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  /** bumped by every write, so the Users and Activity tabs re-read */
  const [changed, setChanged] = useState(0);
  /**
   * The Change plan dialog (PR 2) — opened from the head, the Overview and the
   * Billing tab alike; the Billing tab's "Start free period…" opens it with a
   * free period first switched on.
   */
  const [planOpen, setPlanOpen] = useState<{ freePeriod: boolean } | null>(null);
  /** false on an install with no SMTP — no screen may offer to email a link */
  const [canEmail, setCanEmail] = useState(false);
  /** set only when an invitation could not be emailed — see InviteLinkModal */
  const [handOver, setHandOver] = useState<SentInvite | null>(null);

  const loadAccount = useCallback(() => getAccountApi(accountId)
    .then((r) => { setAccount(r.account); setLoadError(''); })
    .catch((e) => setLoadError((e as Error).message)), [accountId]);
  const loadBilling = useCallback(() => getAccountBilling(accountId)
    .then(setBilling)
    .catch((e) => setError((e as Error).message)), [accountId]);
  const loadRecent = useCallback(() => listPlatformAudit(4, { accountId })
    .then((r) => setRecent(r.entries))
    .catch(() => setRecent([])), [accountId]);

  useEffect(() => {
    setAccount(null); setBilling(null); setNotes(null); setRecent(null);
    void loadAccount();
    void loadBilling();
    void loadRecent();
    listStaffNotesApi(accountId).then((r) => setNotes(r.notes)).catch(() => setNotes([]));
    adminMe().then((r) => setCanEmail(!!r.mailConfigured)).catch(() => undefined);
  }, [accountId, loadAccount, loadBilling, loadRecent]);

  /** After a write: the head, the tiles, the trail, and whichever tab is open. */
  const refresh = useCallback(() => {
    void loadAccount();
    void loadBilling();
    void loadRecent();
    setChanged((n) => n + 1);
  }, [loadAccount, loadBilling, loadRecent]);

  const setTab = (t: Tab) => {
    setMenuOpen(false);
    // replace, not push: tabs are views of one page, and Back leaves the account.
    setParams(t === 'overview' ? {} : { tab: t }, { replace: true, state: location.state });
  };
  const open = (kind: 'masq' | 'status' | 'delete' | 'purge') => {
    setMenuOpen(false);
    setModalError('');
    setEditor({ kind });
  };
  const close = () => { setEditor(null); setModalError(''); };
  const changePlan = () => { setMenuOpen(false); setPlanOpen({ freePeriod: false }); };
  const startFreePeriod = () => { setMenuOpen(false); setPlanOpen({ freePeriod: true }); };

  if (!account) {
    return (
      <div className="pl-page pl-ao">
        <Link to={backTo} className="pl-back">← Accounts</Link>
        <div className="fs-card st-card-body">
          {loadError ? <span className="ad-err">{loadError}</span> : 'Loading…'}
        </div>
      </div>
    );
  }

  const deleting = !!account.delete_after;
  const trialEnds = billing?.trial?.expires_at ?? null;
  const owner = account.owner_email
    ? (account.owner_name ? `${account.owner_name} · ${account.owner_email}` : account.owner_email)
    : 'No owner login';

  // ---- the writes ----
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setModalError('');
    try { await fn(); } catch (e) { setModalError((e as Error).message); }
    setBusy(false);
  }

  const onEnter = (visit: SupportVisit) => run(async () => {
    await masqueradeApi(accountId, visit);
    toast(`Entered ${account.name} ${visit.access === 'read' ? 'read-only' : 'with full access'} · logged`);
    navigate('/admin');
  });

  const onStatus = (status: string) => run(async () => {
    await updateAccountApi(accountId, { status });
    close();
    toast(`${account.name} is now ${(STATUS_WORD[status] ?? status).toLowerCase()}.`);
    refresh();
  });

  const onDelete = () => run(async () => {
    const r = await deleteAccountApi(accountId);
    close();
    toast(`${account.name} will be deleted on ${longDate(localDay(r.deleteAfter))}.`
      + (r.stripe === 'paused' ? ' Their billing is paused.' : ''));
    refresh();
  });

  const onPurge = (typed: string) => run(async () => {
    await purgeAccountApi(accountId, typed);
    close();
    toast(`${account.name} was deleted.`);
    navigate('/platform', { replace: true });
  });

  const onRestore = async () => {
    setMenuOpen(false);
    setError('');
    try {
      await restoreAccountApi(accountId);
      toast(`${account.name} restored.`);
      refresh();
    } catch (e) { setError((e as Error).message); }
  };

  // ---- the head ----
  const menu = (
    <RowMenu
      open={menuOpen}
      onToggle={setMenuOpen}
      label={account.name}
      className="pl-ao-menu"
      renderTrigger={({ open: isOpen, onClick, onKeyDown }) => (
        <button type="button" className={`fs-btn neutral pl-ao-more${isOpen ? ' open' : ''}`} aria-label="More actions"
          aria-haspopup="menu" aria-expanded={isOpen} title="More actions" onClick={onClick} onKeyDown={onKeyDown}>
          ⋯
        </button>
      )}
    >
      <button type="button" onClick={() => setTab('users')}>Logins and passwords</button>
      <button type="button" disabled={!canManage || deleting} onClick={() => open('status')}
        title={!canManage ? ADMIN_ONLY : deleting ? 'Restore the account first' : undefined}>
        Change status…{!canManage && <span className="pl-ao-admin">Admin</span>}
      </button>
      <div className="rule" />
      {deleting ? (
        <button type="button" className="pl-ao-accent" disabled={!canManage} title={!canManage ? ADMIN_ONLY : undefined}
          onClick={() => void onRestore()}>
          Restore account{!canManage && <span className="pl-ao-admin">Admin</span>}
        </button>
      ) : (
        <button type="button" className="danger" disabled={!canManage} title={!canManage ? ADMIN_ONLY : undefined}
          onClick={() => open('delete')}>
          Delete account…{!canManage && <span className="pl-ao-admin">Admin</span>}
        </button>
      )}
      {deleting && isSuper && (
        <button type="button" className="danger" onClick={() => open('purge')}>Delete now…</button>
      )}
    </RowMenu>
  );

  const supportButton = (label: string, extra = '') => (
    <button type="button" className={`fs-btn primary${extra}`} onClick={() => open('masq')} title="Open this account for support">
      {label}
    </button>
  );
  const planButton = (extra = '') => (
    <button type="button" className={`fs-btn neutral${extra}`} disabled={!canManage}
      title={!canManage ? ADMIN_ONLY : undefined} onClick={changePlan}>
      Change plan
    </button>
  );

  return (
    <div className="pl-page pl-ao">
      {phone ? (
        // The design's phone head (screen 15): a back bar with the name, the
        // state and who under it, and the two actions side by side at 46px.
        <>
          <div className="pl-ao-phonebar">
            <Link to={backTo} className="pl-ao-chev" aria-label="Back to Accounts">‹</Link>
            <h1>{account.name}</h1>
          </div>
          <div className="pl-ao-phonemeta">
            <StatePill account={account} trialEnds={trialEnds} />
            <span>{account.owner_email ?? 'No owner login'} · since {sinceOf(account.created_at)}</span>
          </div>
          <div className="pl-ao-phoneacts">
            {supportButton('Support mode ↗', ' lg')}
            {planButton(' lg')}
            {menu}
          </div>
        </>
      ) : (
        <>
          <Link to={backTo} className="pl-back">← Accounts</Link>
          <div className="fs-page-head pl-head pl-ao-head">
            <div className="text">
              <div className="pl-ab-title">
                <h1>{account.name}</h1>
                <StatePill account={account} trialEnds={trialEnds} />
              </div>
              <div className="fs-page-sub">{owner} · customer since {sinceOf(account.created_at)} · /f/{account.slug}</div>
            </div>
            <div className="pl-head-actions">
              {supportButton('Enter in support mode ↗')}
              {planButton()}
              {menu}
            </div>
          </div>
        </>
      )}

      {deleting && (
        <div className="pl-ao-banner" role="status">
          <span>
            <b>Scheduled for deletion on {longDate(localDay(account.delete_after!))}.</b> Forms are offline and nothing is
            charged. {restoreWindow(account.delete_after!)}
          </span>
          <button type="button" className="fs-btn neutral sm" disabled={!canManage}
            title={!canManage ? ADMIN_ONLY : undefined} onClick={() => void onRestore()}>
            Restore account
          </button>
        </div>
      )}
      {!canManage && (
        <div className="pl-ao-rolenote">
          You’re signed in as <b>Staff</b>: you can look around, add notes, and enter accounts read-only or with full
          access. Plan, pricing, status and deletion here need Admin.
        </div>
      )}
      {error && <div className="ad-err" role="alert">{error}</div>}

      {phone ? (
        <div className="fs-seg pl-ao-segtabs" role="tablist" aria-label="Account">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
              className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      ) : (
        <SubTabs className="pl-ao-tabs" aria-label="Account"
          items={TABS.map((t) => ({ label: t.label, active: tab === t.key, onClick: () => setTab(t.key) }))} />
      )}

      {tab === 'overview' && (
        <OverviewTab
          account={account} billing={billing} notes={notes} recent={recent}
          canManage={canManage} phone={phone}
          onChangePlan={changePlan} onChangeStatus={() => open('status')}
          onDelete={() => open('delete')} onRestore={() => void onRestore()}
          onViewAll={() => setTab('activity')}
          onNoteAdded={(note) => {
            setNotes((cur) => [note, ...(cur ?? [])]);
            toast('Note added.');
            void loadRecent();
          }}
        />
      )}
      {tab === 'users' && (
        <UsersTab
          account={account} canManage={canManage} canEmail={canEmail} reloadKey={changed}
          onDone={(msg) => { toast(msg); void loadRecent(); }}
          onHandOver={setHandOver}
        />
      )}
      {tab === 'billing' && (
        <AccountBillingTab
          accountId={accountId} onChanged={refresh} reloadKey={changed}
          onChangePlan={changePlan} onStartFreePeriod={startFreePeriod}
        />
      )}
      {planOpen && canManage && (
        <ChangePlanDialog
          accountId={accountId}
          startWith={planOpen.freePeriod ? 'freePeriod' : undefined}
          onClose={() => setPlanOpen(null)}
          onApplied={(r, name, terms) => { setPlanOpen(null); toast(changeToast(r, name, terms)); refresh(); }}
        />
      )}
      {tab === 'activity' && <ActivityTab accountId={accountId} reloadKey={changed} phone={phone} />}

      {editor?.kind === 'masq' && (
        <SupportModeModal account={account} busy={busy} error={modalError} onClose={close} onEnter={onEnter} />
      )}
      {editor?.kind === 'status' && canManage && (
        <StatusModal account={account} busy={busy} error={modalError} onClose={close} onSave={onStatus} />
      )}
      {editor?.kind === 'delete' && canManage && (
        <DeleteAccountModal account={account} deleteOn={isoPlusDays(todayIso(), 30)} busy={busy} error={modalError}
          onClose={close} onDelete={onDelete} />
      )}
      {editor?.kind === 'purge' && isSuper && (
        <DeleteNowModal account={account} busy={busy} error={modalError} onClose={close} onDelete={onPurge} />
      )}
      {handOver && <InviteLinkModal invite={handOver} onClose={() => setHandOver(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewTab({
  account, billing, notes, recent, canManage, phone,
  onChangePlan, onChangeStatus, onDelete, onRestore, onViewAll, onNoteAdded,
}: {
  account: ConsoleAccount;
  billing: AccountBillingView | null;
  notes: StaffNote[] | null;
  recent: PlatformAuditEntry[] | null;
  canManage: boolean;
  phone: boolean;
  onChangePlan: () => void;
  onChangeStatus: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onViewAll: () => void;
  onNoteAdded: (note: StaffNote) => void;
}) {
  // The deletion grace is a platform rule (Platform › Rules); the code's number stands in.
  const { rules } = usePlatform();
  const deleting = !!account.delete_after;
  const changePlanLink = (
    <button type="button" className="fs-btn text" disabled={!canManage} title={!canManage ? ADMIN_ONLY : undefined}
      onClick={onChangePlan}>
      Change plan…
    </button>
  );

  return (
    <div className="pl-ao-grid">
      <div className="pl-ao-main">
        {/* The phone's overview (screen 15) opens on the Plan & pricing card; the
            tiles are the desktop's, and Next charge is on the Billing tab too. */}
        {billing && !phone ? <PlanTiles account={account} view={billing} /> : null}
        <Card title="Plan & pricing" aside={changePlanLink} className="pl-ao-pricing">
          {billing ? <PlanPricingRows view={billing} deleteAfter={account.delete_after} /> : <div className="st-card-body st-muted">Loading…</div>}
        </Card>
        <Card
          title="Recent activity"
          aside={<button type="button" className="fs-btn text" onClick={onViewAll}>View all</button>}
          className="pl-ao-recent"
        >
          {!recent ? <div className="st-card-body st-muted">Loading…</div> : recent.length === 0 ? (
            <div className="st-card-body st-muted st-empty">Nothing recorded for this account yet.</div>
          ) : (
            <ul className="pl-ao-acts">
              {recent.slice(0, phone ? 3 : 4).map((e) => (
                <li key={e.key}>
                  <ActionPill action={e.action} />
                  <div>
                    <div className="what">{describeForAccount(e)}</div>
                    <div className="who">{actorName(e.actor_email)} · {ago(e.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="pl-ao-side">
        <StaffNotesCard accountId={account.id} notes={notes} phone={phone} onAdded={onNoteAdded} />
        <Card
          title="Status"
          aside={(
            <button type="button" className="fs-btn text" disabled={!canManage || deleting}
              title={!canManage ? ADMIN_ONLY : deleting ? 'Restore the account first' : undefined} onClick={onChangeStatus}>
              Change…
            </button>
          )}
          className="pl-ao-status"
        >
          <div className="st-card-body">
            <span className={`fs-pill ${STATUS_INTENT[account.status] || 'neutral'}`}>{STATUS_WORD[account.status] ?? account.status}</span>
            <p className="pl-ao-status-text">
              {deleting ? 'Suspended while the account waits to be deleted — Restore puts its status back.' : STATUS_CONSEQUENCE[account.status]}
            </p>
          </div>
        </Card>
        <section className="fs-card pl-ao-card pl-ao-danger">
          <div className="st-card-body">
            <h2>{deleting ? 'Scheduled for deletion' : 'Delete account'}</h2>
            <p>
              {deleting
                ? `Deletes permanently on ${longDate(localDay(account.delete_after!))}. Forms are offline and nothing is charged. Restore any time before then.`
                : `Suspends the account straight away and deletes it after ${rules?.deleteGraceDays ?? 30} days. It can be restored until then.`}
            </p>
            {deleting ? (
              <button type="button" className="fs-btn neutral pl-ao-restore" disabled={!canManage}
                title={!canManage ? ADMIN_ONLY : undefined} onClick={onRestore}>
                Restore account
              </button>
            ) : (
              <button type="button" className="fs-btn danger" disabled={!canManage}
                title={!canManage ? ADMIN_ONLY : undefined} onClick={onDelete}>
                Delete account…
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

/** Plan · MRR · Next charge (screen 02), from the billing view. */
function PlanTiles({ account, view }: { account: ConsoleAccount; view: AccountBillingView }) {
  const { plan, price, mrr, stripe } = view;
  const trial = view.trial as TrialRow | null;
  const deleting = !!account.delete_after;
  const pct = percentRunning(view.account) ? view.account.discount_percent : null;
  const agreed = view.account.price_override_cents !== null;
  // A free period that converts into terms set in Change plan (076) charges
  // those terms first — the tiles name that price, as the rows do.
  const pending = trial?.auto_convert && !deleting ? pendingPriceOf(view) : null;
  const convert = !trial?.auto_convert ? ''
    : pending ? (pending.totalCents !== null ? `${money(pending.totalCents, pending.currency)}${pending.interval === 'year' ? '/yr' : '/mo'}` : '')
      : convertPrice(view.account, trialPlanOf(view, trial));

  const planCaption = deleting
    ? { text: `until deletion on ${monD(localDay(account.delete_after!))}`, tone: 'danger' as const }
    : trial
      ? { text: `free until ${monDY(trial.expires_at)}`, tone: 'info' as const }
      : { text: price.cents ? `billed ${price.interval === 'year' ? 'annually' : 'monthly'}` : 'no charge', tone: undefined };

  const mrrCaption = deleting ? 'nothing renews'
    : trial && convert ? `→ ${convert} from ${monD(trial.expires_at)}`
      : agreed ? 'agreed price'
        : pct ? `${pct}% off`
          : mrr.cents ? 'list price' : 'no charge';

  const next = deleting
    ? { value: 'None', caption: 'Scheduled for deletion' }
    : trial
      ? trial.auto_convert
        ? { value: monD(trial.expires_at), caption: `${convert || trial.plan_name} · first charge` }
        : { value: 'None', caption: `free period ends ${monD(trial.expires_at)}` }
      : stripe.nextInvoice
        ? { value: monD(stripe.nextInvoice.date), caption: moneyExact(stripe.nextInvoice.amount_cents) }
        : { value: 'None', caption: price.cents ? 'no subscription' : 'Free plan' };

  return (
    <div className="pl-tiles pl-ao-tiles">
      <FigureTile label="Plan" value={trial ? trial.plan_name : plan.name} caption={planCaption.text} tone={planCaption.tone} />
      <FigureTile label="MRR" value={money(mrr.cents, plan.currency)} caption={mrrCaption} />
      <FigureTile label="Next charge" value={next.value} caption={next.caption} />
    </div>
  );
}

/** Notes staff leave each other (074): only platform staff see them, and nobody edits one. */
function StaffNotesCard({ accountId, notes, phone, onAdded }: {
  accountId: number;
  notes: StaffNote[] | null;
  phone: boolean;
  onAdded: (note: StaffNote) => void;
}) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const text = draft.trim();
  const add = async () => {
    if (!text || saving) return;
    setSaving(true);
    setError('');
    try {
      const r = await addStaffNoteApi(accountId, text);
      setDraft('');
      onAdded(r.note);
    } catch (e) { setError((e as Error).message); }
    setSaving(false);
  };

  return (
    <Card
      title="Staff notes"
      aside={<span className="pl-ao-aside">{phone ? 'Staff only' : 'Only platform staff see these'}</span>}
      className="pl-ao-notes"
    >
      <div className={`pl-ao-note-form${phone ? ' inline' : ''}`}>
        {phone ? (
          <input className="fs-input" value={draft} aria-label="Add a note for the team" placeholder="Add a note for the team…"
            onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} />
        ) : (
          <textarea className="pl-ao-note-input" value={draft} aria-label="Add a note for the team"
            placeholder="Add a note for the team…" onChange={(e) => setDraft(e.target.value)} />
        )}
        <button type="button" className="fs-btn neutral sm" disabled={!text || saving} onClick={() => void add()}>
          {phone ? 'Add' : 'Add note'}
        </button>
      </div>
      {error && <div className="ad-err" role="alert">{error}</div>}
      {!notes ? <div className="pl-ao-note-empty">Loading…</div> : notes.length === 0 ? (
        <div className="pl-ao-note-empty">No notes yet.</div>
      ) : (
        <ul className="pl-ao-note-list">
          {notes.map((n) => (
            <li key={n.id}>
              <div className="body">{n.body}</div>
              <div className="who">{n.author_name || n.author_email} · {ago(n.created_at)}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

const roleWord = (role: string) => role.charAt(0).toUpperCase() + role.slice(1);

/**
 * The account's logins (screen 03), where the "Logins and passwords…" dialog
 * used to be. Staff read the list — knowing who is in an account is support's
 * job — while adding a login, a reset and a resend are Admin and above, as the
 * routes enforce.
 */
function UsersTab({ account, canManage, canEmail, reloadKey, onDone, onHandOver }: {
  account: ConsoleAccount;
  canManage: boolean;
  canEmail: boolean;
  reloadKey: number;
  onDone: (message: string) => void;
  /** an invitation that could not be emailed, for the page to hand over */
  onHandOver: (invite: SentInvite) => void;
}) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState('');
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => listAccountUsersApi(account.id)
    .then((r) => setUsers(r.users))
    .catch((e) => setError((e as Error).message)), [account.id]);
  useEffect(() => { void load(); }, [load, reloadKey]);

  async function reset(user: AdminUser, body: PasswordReset) {
    setError('');
    try {
      await platformResetPasswordApi(user.id, body);
      setResetting(null);
      onDone('sendLink' in body
        ? `Sent ${user.email} a link to set a new password.`
        : `Set a new password for ${user.email} — they are signed out everywhere until they use it.`);
    } catch (e) { setError((e as Error).message); }
  }

  /** Somebody who has never signed in is re-invited, not reset. */
  async function resend(user: AdminUser) {
    setError('');
    try {
      const r = await platformResendInviteApi(user.id);
      if (r.invite.emailed) onDone(invitedMessage(r.invite));
      else onHandOver(r.invite);
      void load();
    } catch (e) { setError((e as Error).message); }
  }

  async function add(body: Record<string, unknown>) {
    setError('');
    try {
      const r = await addAccountUserApi(account.id, body);
      setAdding(false);
      if (r.invite && !r.invite.emailed) onHandOver(r.invite);
      else if (r.invite) onDone(invitedMessage(r.invite));
      void load();
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <>
      <TableCard
        className="pl-ao-users"
        title="Logins"
        action={(
          <button type="button" className="fs-btn text" disabled={!canManage} title={!canManage ? ADMIN_ONLY : undefined}
            onClick={() => setAdding(true)}>
            + Add user
          </button>
        )}
      >
        {error && <div className="ad-err pl-ao-card-err" role="alert">{error}</div>}
        {!users ? <div className="st-card-body st-muted">Loading…</div> : users.length === 0 ? (
          <div className="st-card-body st-muted st-empty">This account has no logins at all — add one with + Add user.</div>
        ) : (
          <table className="fs-table">
            <thead>
              <tr><th>Person</th><th>Role</th><th>Last sign-in</th><th className="fs-row-actions" /></tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const pending = isInvitePending(u);
                return (
                  <tr key={u.id} className={u.active === false ? 'pl-ao-inactive' : undefined}>
                    <td>
                      <div className="pl-acct-name">{u.name || u.email}</div>
                      {u.name && <div className="fs-cell-sub faint">{u.email}</div>}
                    </td>
                    <td>
                      <span className={`fs-pill ${u.active === false || pending ? 'neutral' : 'ok'}`}>
                        {u.active === false ? 'disabled' : roleWord(u.role)}
                      </span>
                    </td>
                    <td className="pl-ao-signin">
                      {u.last_signin_at
                        ? new Date(u.last_signin_at).toLocaleDateString()
                        : pending ? 'invited, not accepted' : 'never'}
                    </td>
                    <td className="fs-row-actions">
                      {pending ? (
                        <button type="button" className="fs-btn neutral sm" disabled={!canManage}
                          title={!canManage ? ADMIN_ONLY : undefined} onClick={() => void resend(u)}>
                          Resend invitation
                        </button>
                      ) : (
                        <button type="button" className="fs-btn neutral sm" disabled={!canManage}
                          title={!canManage ? ADMIN_ONLY : undefined} onClick={() => setResetting(u)}>
                          Reset password…
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </TableCard>
      <div className="pl-ao-note">
        A reset lands in the audit trail under your name. Where this server can send mail it is always a link only they
        can use — you never learn anybody’s password.
      </div>
      {resetting && (
        <ResetPasswordModal user={resetting} canEmail={canEmail}
          onClose={() => setResetting(null)} onReset={(body) => reset(resetting, body)} />
      )}
      {adding && <AddUserModal account={account} onClose={() => setAdding(false)} onAdd={(body) => void add(body)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

const ACTIVITY_PAGE = 50;

/**
 * Everything staff did to this account, newest first (screen 05): plans,
 * pricing, free periods, credits, statuses, support visits, logins, notes —
 * the platform trail narrowed to the account, fifty at a go with Load more.
 * It holds what the billing page's History held, and the rest besides.
 */
function ActivityTab({ accountId, reloadKey, phone }: { accountId: number; reloadKey: number; phone: boolean }) {
  const [entries, setEntries] = useState<PlatformAuditEntry[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const asked = useRef(0);

  const load = useCallback(async (before: string | null) => {
    const ticket = ++asked.current;
    setBusy(true);
    try {
      const r = await listPlatformAudit(ACTIVITY_PAGE, { accountId, before });
      if (ticket !== asked.current) return;
      setEntries((cur) => (before && cur ? [...cur, ...r.entries] : r.entries));
      setNext(r.nextCursor ?? null);
      setError('');
    } catch (e) {
      if (ticket === asked.current) setError((e as Error).message || 'Could not load the activity.');
    } finally {
      if (ticket === asked.current) setBusy(false);
    }
  }, [accountId]);

  useEffect(() => { void load(null); }, [load, reloadKey]);

  return (
    <TableCard
      className="pl-ao-activity"
      title="Activity on this account"
      hint="Plans, pricing, statuses, masquerades, logins. Append-only."
      footer={entries && entries.length > 0 && (next || error) ? (
        <>
          {next && (
            <button type="button" className="fs-btn neutral sm" disabled={busy} onClick={() => void load(next)}>
              {busy ? 'Loading…' : 'Load more'}
            </button>
          )}
          {error && <span className="pl-ab-blocker">{error}</span>}
        </>
      ) : undefined}
    >
      {!entries ? (
        error ? <div className="st-card-body st-muted st-empty">{error}</div>
          : <div className="st-card-body st-muted">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="st-card-body st-muted st-empty">Nothing recorded for this account yet.</div>
      ) : <AuditTable entries={entries} phone={phone} describe={describeForAccount} />}
    </TableCard>
  );
}
